<template>
  <div>
    <div class="card" style="padding:16px; margin-bottom:16px; display:flex; align-items:center; justify-content:space-between;">
      <div>
        <div style="color:var(--muted); font-size:12px;">Logged in as</div>
        <div style="font-weight:600;">{{ userEmail }}</div>
      </div>
      <div>
        <button class="btn" @click="logout" title="Logout" style="padding:8px; display:flex; align-items:center; background: #e2e8f0; color: #334155;">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
        </button>
      </div>
    </div>

    <div style="border-bottom: 1px solid var(--border); margin-bottom: 24px; display: flex; gap: 24px;">
      <div 
        @click="activeTab = 'online'"
        style="padding-bottom: 12px; cursor: pointer; font-weight: 500; transition: all 0.2s;"
        :style="activeTab === 'online' ? 'color:var(--primary); border-bottom: 2px solid var(--primary);' : 'color:var(--muted); border-bottom: 2px solid transparent;'"
      >
        Online Mode
      </div>
      <div 
        @click="activeTab = 'offline'"
        style="padding-bottom: 12px; cursor: pointer; font-weight: 500; transition: all 0.2s;"
        :style="activeTab === 'offline' ? 'color:var(--primary); border-bottom: 2px solid var(--primary);' : 'color:var(--muted); border-bottom: 2px solid transparent;'"
      >
        Offline Mode
      </div>
    </div>

    <div v-if="activeTab === 'online'">
      <div class="grid cols-4" style="margin-top:16px;">
      <InsightCard title="Total Accounts" :value="accountCounts.total"/>
      <InsightCard title="Activated Accounts" :value="accountCounts.paid"/>
      <InsightCard title="In Trial Accounts" :value="accountCounts.trial"/>
      <InsightCard title="Canceled Accounts" :value="accountCounts.canceled"/>
    </div>

    <div class="card" style="padding:16px; margin-top:16px;">
      <h3 style="margin:0 0 16px 0;">Commission Calculator</h3>
      <div style="display:flex; flex-wrap:wrap; gap:24px;">
        <div style="flex:1; min-width:250px;">
          <div class="label">Price</div>
          <input type="number" class="input" v-model.number="calcPrice" placeholder="Enter price" />
          
          <div style="margin-top:12px; display:flex; align-items:center; gap:8px;">
            <input type="checkbox" id="tva-toggle" v-model="calcTva" />
            <label for="tva-toggle">Apply 20% TVA</label>
          </div>
        </div>
        
        <div style="flex:1; min-width:250px; background:var(--bg-subtle, #f8fafc); padding:16px; border-radius:8px;">
          <div style="display:flex; justify-content:space-between; margin-bottom:8px;">
            <span style="font-weight:600;">Total</span>
            <span style="font-weight:600;">{{ formatNumber(calcPrice) }}</span>
          </div>
          
          <div style="display:flex; justify-content:space-between; margin-bottom:8px; color:var(--muted); font-size:14px;">
            <span>- TVA ({{ calcTva ? '20%' : '0%' }})</span>
            <span style="color:#ef4444;">{{ formatNumber(tvaAmount) }}</span>
          </div>
          
          <div style="display:flex; justify-content:space-between; margin-bottom:8px; color:var(--muted); font-size:14px;">
            <span>- Fixed charges (servers / support / services)</span>
            <span style="color:#ef4444;">{{ formatNumber(serverChargeAmount) }}</span>
          </div>

          <div style="border-top:1px solid var(--border); margin:8px 0;"></div>
          
          <div style="display:flex; justify-content:space-between; margin-bottom:16px; font-weight:600;">
            <span>Amount after deduction</span>
            <span>{{ formatNumber(netAmount) }}</span>
          </div>
          
          <div style="background:#10b981; color:white; padding:12px; border-radius:6px; display:flex; justify-content:space-between; align-items:center;">
            <span style="font-weight:500;">Your Commission (40%)</span>
            <span style="font-size:18px; font-weight:700;">{{ formatNumber(commissionAmount) }}</span>
          </div>
        </div>
      </div>
    </div>



    <div class="card" style="padding:16px; margin-top:16px;">
      <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:12px;">
        <h3 style="margin:0;">Accounts</h3>
        <button class="btn" @click="openCreateAccountModal">Create Account</button>
      </div>
      <div style="overflow:auto;">
        <table style="width:100%; border-collapse:collapse;">
          <thead>
            <tr style="text-align:left; color:var(--muted);">
              <!-- <th style="padding:8px; border-bottom:1px solid var(--border);">ID</th> -->
              <th style="padding:8px; border-bottom:1px solid var(--border);">Name</th>
              <th style="padding:8px; border-bottom:1px solid var(--border);">Email</th>
              <th style="padding:8px; border-bottom:1px solid var(--border);">Phone</th>
              <th style="padding:8px; border-bottom:1px solid var(--border);">Status</th>

              <th style="padding:8px; border-bottom:1px solid var(--border);">Created At</th>
              <th style="padding:8px; border-bottom:1px solid var(--border); text-align:right;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="acc in accounts" :key="acc.id">
              <!-- <td style="padding:8px; border-bottom:1px solid var(--border);">{{ acc.id }}</td> -->
              <td style="padding:8px; border-bottom:1px solid var(--border);">{{ acc.name }}</td>
              <td style="padding:8px; border-bottom:1px solid var(--border);">{{ acc.email }}</td>
              <td style="padding:8px; border-bottom:1px solid var(--border);">{{ acc.phone }}</td>
              <td style="padding:8px; border-bottom:1px solid var(--border);">
                <span :style="statusBadge(acc.status)">{{ acc.status }}</span>
              </td>

              <td style="padding:8px; border-bottom:1px solid var(--border);">{{ timeAgo(acc.createdAt) }}</td>
              <td style="padding:8px; border-bottom:1px solid var(--border); text-align:right;">
                <button class="btn" @click="loginAsUser(acc)" style="margin-right:8px; padding:6px 10px; background: #e2e8f0; color: #334155;" title="Login as User">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path><polyline points="10 17 15 12 10 7"></polyline><line x1="15" y1="12" x2="3" y2="12"></line></svg>
                </button>
                <button class="btn" @click="openEditModal(acc)" style="padding:6px 10px; background: #e2e8f0; color: #334155;" title="Edit Account">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    </div>

    <div v-if="activeTab === 'offline'">
      <div class="card" style="padding:24px; display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:300px;">
        <h2 style="margin-top:0;">Offline License Generator</h2>
        <p style="color:var(--muted); max-width:400px; text-align:center; margin-bottom:24px;">
          Generate a license key for offline activation. This key can be used to activate the software without an internet connection.
        </p>
        <button class="btn" style="padding:12px 24px; font-size:16px;" @click="openLicenseModal" :disabled="generatingGlobal"> 
          <span v-if="generatingGlobal">Generating...</span>
          <span v-else>Generate Offline License</span>
        </button>
      </div>

      <div class="card" style="padding:16px; margin-top:16px;">
        <h3 style="margin:0 0 12px 0;">Serials</h3>
        <div style="overflow:auto;">
          <table style="width:100%; border-collapse:collapse;">
            <thead>
              <tr style="text-align:left; color:var(--muted);">
                <!-- <th style="padding:8px; border-bottom:1px solid var(--border);">ID</th> -->
                <th style="padding:8px; border-bottom:1px solid var(--border);">Serial Key</th>
                <th style="padding:8px; border-bottom:1px solid var(--border);">Status</th>
                <th style="padding:8px; border-bottom:1px solid var(--border);">Created At</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in serials" :key="s.id">
                <!-- <td style="padding:8px; border-bottom:1px solid var(--border);">{{ s.id }}</td> -->
                <td style="padding:8px; border-bottom:1px solid var(--border); font-family:monospace;">{{ s.serial_key }}</td>
                <td style="padding:8px; border-bottom:1px solid var(--border);">
                  <span :style="statusBadge(s.status)">{{ s.status }}</span>
                </td>
                <td style="padding:8px; border-bottom:1px solid var(--border);">{{ formatDate(s.created_at || s.createdAt) }}</td>
              </tr>
              <tr v-if="!serials.length">
                <td colspan="4" style="padding:12px; color:var(--muted);">No serials yet.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="error" class="card" style="padding:12px; margin-top:16px; color:#fca5a5;">{{ error }}</div>

    <!-- License Modal -->
    <div v-if="licenseModalOpen" style="position:fixed; inset:0; background: rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index:50;">
      <div class="card" style="width: min(600px, 92vw); padding:16px;">
        <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:8px;">
          <h3 style="margin:0;">Generated License Key</h3>
          <button class="btn" @click="closeLicenseModal">Close</button>
        </div>
        <p style="color:var(--muted); margin-top:0;">Use this key to activate the account. Format: XXXXX-XXXXX-XXXXX-XXXXX-XXXXX</p>
        <div style="display:flex; gap:8px; align-items:center;">
          <input :value="globalLicenseKey" readonly class="input" style="flex:1; font-weight:700; letter-spacing:2px;" placeholder="—" />
          <button class="btn" @click="copyGlobal" :disabled="!globalLicenseKey">
            <span v-if="copiedGlobal">Copied!</span>
            <span v-else>Copy</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Create Account Modal -->
    <div v-if="createAccountModalOpen" style="position:fixed; inset:0; background: rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index:50;">
      <div class="card" style="width: min(640px, 95vw); padding:16px;">
        <div style="display:flex; align-items:center; justify-content:space-between;">
          <h3 style="margin:0;">Create Account</h3>
          <button class="btn" @click="closeCreateAccountModal">Close</button>
        </div>
        <div style="margin-top:12px; display:grid; grid-template-columns: 1fr 1fr; grid-gap:12px;">
          <div style="grid-column: span 2;">
            <div class="label">Business Name</div>
            <input type="text" class="input" v-model="newAccount.business_name" placeholder="Business Name" />
          </div>
          <div style="grid-column: span 2;">
            <div class="label">Contact Name <span style="color:#ef4444">*</span></div>
            <input type="text" class="input" v-model="newAccount.name" placeholder="Full Name" />
          </div>
          <div>
            <div class="label">Email <span style="color:#ef4444">*</span></div>
            <input type="email" class="input" v-model="newAccount.email" placeholder="Email Address" />
          </div>
          <div>
            <div class="label">Password <span style="color:#ef4444">*</span></div>
            <input type="password" class="input" v-model="newAccount.password" placeholder="Password" />
          </div>
          <div>
            <div class="label">Phone</div>
            <input type="tel" class="input" v-model="newAccount.phone" placeholder="Phone Number" />
          </div>
          <div>
            <div class="label">Website</div>
            <input type="url" class="input" v-model="newAccount.website" placeholder="https://..." />
          </div>
          <div>
            <div class="label">Currency</div>
            <select class="input" v-model="newAccount.currency">
              <option v-for="(symbol, country) in currencyMap" :key="country" :value="symbol">
                {{ formatCountryName(country) }} ({{ symbol }})
              </option>
            </select>
          </div>
          <div style="grid-column: span 2;">
            <div class="label">Address</div>
            <input type="text" class="input" v-model="newAccount.address" placeholder="Business Address" />
          </div>
        </div>
        <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:14px;">
          <button class="btn" @click="createAccount" :disabled="creatingAccount">
            <span v-if="creatingAccount">Creating...</span>
            <span v-else>Create Account</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Account Created Modal -->
    <div v-if="accountCreatedModalOpen" style="position:fixed; inset:0; background: rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index:50;">
      <div class="card" style="width: min(400px, 90vw); padding:24px; text-align:center;">
        <h3 style="margin-top:0; color: #10b981;">Account Created!</h3>
        <p style="color:var(--muted);">
          The account for <strong>{{ createdAccountData?.user?.name }}</strong> has been successfully created.
        </p>
        <div style="display:flex; flex-direction:column; gap:12px; margin-top:20px;">
          <button class="btn" @click="loginWithCreatedAccount">
            Login as {{ createdAccountData?.user?.name }}
          </button>
          <button class="btn" style="background:transparent; border:1px solid var(--border); color:var(--muted);" @click="accountCreatedModalOpen = false">
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Account Modal -->
    <div v-if="editModalOpen" style="position:fixed; inset:0; background: rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index:50;">
      <div class="card" style="width: min(640px, 95vw); padding:16px;">
        <div style="display:flex; align-items:center; justify-content:space-between;">
          <h3 style="margin:0;">Edit Account</h3>
          <button class="btn" @click="closeEditModal">Close</button>
        </div>
        <div style="margin-top:12px; display:grid; grid-template-columns: 1fr; grid-gap:12px;">
          <div>
            <div class="label">New Status</div>
            <select class="input" v-model="editStatus">
              <option value="active">active</option>
              <option value="trial">trial</option>
              <option value="canceled">canceled</option>
            </select>
          </div>
        </div>
        <div style="display:flex; justify-content:flex-end; gap:8px; margin-top:14px;">
          <button class="btn" @click="saveEdit" :disabled="savingEdit">
            <span v-if="savingEdit">Saving...</span>
            <span v-else>Save Changes</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InsightCard from '@/components/InsightCard.vue'
import { insights } from '@/services/insights'

const currencyMap = {
  'morocco': 'dh',
  'france': '€',
  'united_states': '$',
  'germany': '€',
  'egypt': 'EGP',
  'spain': '€',
  'united_kingdom': '£',
  'italy': '€',
  'netherlands': '€',
  'algeria': 'DZD',
  'tunisia': 'DT',
  'angola': 'AOA',
  'benin': 'XOF',
  'botswana': 'BWP',
  'democratic_republic_of_congo': 'CDF',
  'central_african_republic': 'XAF',
  'republic_of_congo': 'XAF',
  'cote_d_ivoire': 'XOF',
  'djibouti': 'DJF',
  'eritrea': 'ERN',
  'ethiopia': 'ETB',
  'gabon': 'XAF',
  'ghana': 'GHS',
  'gambia': 'GMD',
  'guinea': 'GNF',
  'equatorial_guinea': 'XAF',
  'kenya': 'KES',
  'comoros': 'KMF',
  'liberia': 'LRD',
  'lesotho': 'LSL',
  'libya': 'LYD',
  'madagascar': 'MGA',
  'mali': 'XOF',
  'mauritania': 'MRU',
  'malawi': 'MWK',
  'mauritius': 'MUR',
  'mozambique': 'MZN',
  'namibia': 'NAD',
  'niger': 'XOF',
  'nigeria': 'NGN',
  'réunion': '€',
  'rwanda': 'RWF',
  'seychelles': 'SCR',
  'sudan': 'SDG',
  'saint_helena': 'SHP',
  'sierra_leone': 'SLL',
  'senegal': 'XOF',
  'somalia': 'SOS',
  'south_sudan': 'SSP',
  'são_tomé_and_príncipe': 'STN',
  'swaziland': 'SZL',
  'chad': 'XAF',
  'togo': 'XOF',
  'tanzania': 'TZS',
  'uganda': 'UGX',
  'south_africa': 'R',
  'zambia': 'ZMW',
  'zimbabwe': 'ZWL'
}

export default {
  name: 'AffiliateDashboard',
  components: { InsightCard },
  data() {
    return {
      activeTab: 'online',
      calcPrice: 0,
      calcTva: true,
      accountCounts: { total: 0, active: 0, trial: 0, canceled: 0 },
      accounts: [],
      serials: [],
      loading: false,
      error: '',
      globalLicenseKey: '',
      generatingGlobal: false,
      copiedGlobal: false,
      licenseModalOpen: false,
      editModalOpen: false,
      editTarget: null,
      editStatus: 'trial',

      savingEdit: false,
      affiliateLink: '',

      copiedAffiliate: false,
      createAccountModalOpen: false,
      currencyMap,
      newAccount: {
        business_name: '',
        name: '',
        email: '',
        password: '',
        phone: '',
        website: '',
        address: '',
        currency: 'dh'
      },
      creatingAccount: false,
      accountCreatedModalOpen: false,
      createdAccountData: null
    }
  },
  computed: {
    userEmail() {
      return localStorage.getItem('affiliate_email') || ''
    },
    tvaAmount() {
      if (!this.calcPrice) return 0
      return this.calcTva ? this.calcPrice * 0.20 : 0
    },
    serverChargeAmount() {
      if (!this.calcPrice) return 0
      // Server charges (10%) calculated after TVA deduction
      const afterTva = this.calcPrice - this.tvaAmount
      return afterTva * 0.10
    },
    netAmount() {
      if (!this.calcPrice) return 0
      return this.calcPrice - this.tvaAmount - this.serverChargeAmount
    },
    commissionAmount() {
      return this.netAmount * 0.40
    }
  },
  methods: {
    formatNumber(value) {
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
    },
    formatCountryName(name) {
      return name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
    },
    logout() {
      if (!confirm('Are you sure you want to logout?')) return
      localStorage.removeItem('affiliate_token')
      localStorage.removeItem('affiliate_email')
      this.$router.push('/login')
    },
    async loadInsights() {
      this.error = ''
      try {
        this.accountCounts = await insights.getAccountCounts()
      } catch (e) {
        this.error = e?.message || 'Failed to load insights'
      }
    },
    async loadAccounts() {
      this.error = ''
      try {
        this.accounts = await insights.getAccounts()
      } catch (e) {
        this.error = e?.message || 'Failed to load accounts'
      }
    },
    async loadSerials() {
      try {
        this.serials = await insights.getSerials()
      } catch (e) {
        // keep serials empty on failure; don't block others
      }
    },
    async loginAsUser(acc) {
      if (!confirm(`Are you sure you want to login as ${acc.name}?`)) return
      try {
        const data = await insights.loginAsUser(acc.id)
        if (data && data.token) {
          // Use environment variable or default to production dashboard
          const baseUrl = process.env.VUE_APP_DASH_URL || 'https://dash.gstocky.com'
          const affiliateId = localStorage.getItem('affiliate_id') || ''
          const url = `${baseUrl}?at=${data.token}&acc_man=true&affiliate_id=${affiliateId}`
          window.open(url, '_blank')
        } else if (data && data.url) {
          window.open(data.url, '_blank')
        } else {
          // Fallback if no URL returned, maybe show a message or handle token
        }
      } catch (e) {
        this.error = e?.message || 'Failed to login as user'
      }
    },
    openCreateAccountModal() {
      this.newAccount = { business_name: '', name: '', email: '', password: '', phone: '', website: '', address: '', currency: 'dh' }
      this.createAccountModalOpen = true
    },
    closeCreateAccountModal() {
      this.createAccountModalOpen = false
    },
    async createAccount() {
      this.creatingAccount = true
      this.error = ''
      try {
        const data = await insights.createAccount(this.newAccount)
        this.createdAccountData = data
        this.closeCreateAccountModal()
        this.loadAccounts()
        this.loadInsights()
        this.accountCreatedModalOpen = true
      } catch (e) {
        this.error = e?.message || 'Failed to create account'
      } finally {
        this.creatingAccount = false
      }
    },
    loginWithCreatedAccount() {
      if (!this.createdAccountData || !this.createdAccountData.token) return
      const token = this.createdAccountData.token
      const baseUrl = process.env.VUE_APP_DASH_URL || 'https://dash.gstocky.com'
      const affiliateId = localStorage.getItem('affiliate_id') || ''
      const url = `${baseUrl}?at=${token}&acc_man=true&affiliate_id=${affiliateId}`
      window.open(url, '_blank')
      this.accountCreatedModalOpen = false
    },
    openEditModal(acc) {
      this.editTarget = acc
      this.editStatus = acc.status

      this.editModalOpen = true
    },
    closeEditModal() {
      this.editModalOpen = false
      this.editTarget = null
      this.editDays = 0
    },
    async saveEdit() {
      if (!this.editTarget) return
      this.savingEdit = true
      try {
        // Update status if changed
        if (this.editStatus && this.editStatus !== this.editTarget.status) {
          const updated = await insights.updateAccountStatus(this.editTarget.id, this.editStatus)
          this.editTarget.status = updated
        }

        this.closeEditModal()
      } catch (e) {
        this.error = e?.message || 'Failed to save changes'
      } finally {
        this.savingEdit = false
      }
    },
    async onGenerateGlobal() {
      this.generatingGlobal = true
      try {
        this.globalLicenseKey = await insights.generateLicenseKey()
      } catch (e) {
        this.error = e?.message || 'Failed to generate license key'
      } finally {
        this.generatingGlobal = false
      }
    },
    async openLicenseModal() {
      await this.onGenerateGlobal()
      this.licenseModalOpen = true
    },
    closeLicenseModal() {
      this.licenseModalOpen = false
    },
    async copyGlobal() {
      const key = this.globalLicenseKey
      if (!key) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(key)
        } else {
          const ta = document.createElement('textarea')
          ta.value = key
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copiedGlobal = true
        setTimeout(() => { this.copiedGlobal = false }, 1200)
      } catch (e) {
        // ignore copy errors
      }
    },
    statusBadge(status) {
      const map = {
        active: { bg: '#064e3b', color: '#a7f3d0' },
        trial: { bg: '#4338ca', color: '#c7d2fe' },
        canceled: { bg: '#7f1d1d', color: '#fecaca' }
      }
      const s = map[status] || { bg: '#374151', color: '#e5e7eb' }
      return { background: s.bg, color: s.color, padding: '2px 8px', borderRadius: '999px', fontSize: '12px' }
    },
    formatDate(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (isNaN(d.getTime())) return '—'
      try {
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })
      } catch (e) {
        return d.toLocaleDateString()
      }
    },
    daysLeft(value) {
      if (!value) return '—'
      const end = new Date(value)
      if (isNaN(end.getTime())) return '—'
      const now = new Date()
      // Calculate full days difference, rounding up to show partial day as 1 day
      const ms = end.getTime() - now.getTime()
      const days = Math.ceil(ms / (1000 * 60 * 60 * 24))
      if (days > 1) return `${days} days left`
      if (days === 1) return '1 day left'
      if (days === 0) return 'Ends today'
      return 'Expired'
    },
    timeAgo(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (isNaN(date.getTime())) return '—'
      const ms = date.getTime() - Date.now()
      const rtf = (typeof Intl !== 'undefined' && Intl.RelativeTimeFormat)
        ? new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
        : null
      const minutes = Math.round(ms / (1000 * 60))
      const hours = Math.round(ms / (1000 * 60 * 60))
      const days = Math.round(ms / (1000 * 60 * 60 * 24))
      if (rtf) {
        if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute')
        if (Math.abs(hours) < 24) return rtf.format(hours, 'hour')
        return rtf.format(days, 'day')
      }
      // Fallback simple text
      const abs = (n) => Math.abs(n)
      if (abs(minutes) < 60) return `${abs(minutes)} min${abs(minutes) === 1 ? '' : 's'} ago`
      if (abs(hours) < 24) return `${abs(hours)} hour${abs(hours) === 1 ? '' : 's'} ago`
      return `${abs(days)} day${abs(days) === 1 ? '' : 's'} ago`
    },
    async refreshAll() {
      this.loading = true
      try {
        await Promise.all([
          this.loadInsights(),
          this.loadAccounts(),
          this.loadSerials(),
          this.loadAffiliateLink(),
        ])
      } finally {
        this.loading = false
      }
    },
    async loadAffiliateLink() {
      try {
        this.affiliateLink = await insights.getAffiliateLink()
      } catch (e) {
        // ignore; keep placeholder
      }
    },
    async copyAffiliateLink() {
      const link = this.affiliateLink
      if (!link) return
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(link)
        } else {
          const ta = document.createElement('textarea')
          ta.value = link
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        this.copiedAffiliate = true
        setTimeout(() => { this.copiedAffiliate = false }, 1200)
      } catch (e) {
        // ignore copy errors
      }
    }
  },
  created() {
    this.refreshAll()
    if(localStorage.getItem('affiliate_email')  == 'abubaker@gstocky.com'){
      localStorage.removeItem('affiliate_email')
      localStorage.removeItem('affiliate_token')
      this.$router.push('/login')
    }
  }
}
</script>
